import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	FieldExpression, NodeExpression, term, toString as luceneToString
} from 'lucene';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { ORDER_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy } from '../../shared/utils/constant';
import { AggregateBatch, Batch, batchCardFields, batchShipmentFields, FieldOfBatch } from '../models/batch';
import { DatacenterFilterType } from '../../shared/models/datacenter-filters';
import { UserDatacenterView } from '../../shared/models/datacenter-aggrid-view';
import { Document } from '../../commercial-documents/models/document';

const GET_BATCHES_URL = `${ ORDER_SERVER_URL }/v5/batches`;
const GET_DATACENTER_BATCHES_URL_V5 = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;

interface IBatchesService {
	getBatches(
		fields: readonly FieldOfBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Batch>[] | OrderBy<keyof Batch>,
		onlyDistinct: boolean
	): Observable<PaginatedResult<Batch>>;
}

@Injectable({ providedIn: 'root' })
export class BatchesService implements IBatchesService {
	millsFromBatches?: Batch;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {}

	getBatches(
		fields: readonly FieldOfBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Batch>[] | OrderBy<keyof Batch>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_BATCHES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<Batch>>(url.toString());
	}

	getDatacenterBatchesV5(
		fields: readonly FieldOfBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Batch>[] | OrderBy<keyof Batch>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_DATACENTER_BATCHES_URL_V5);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<Batch>>(url.toString());
	}

	buildOrderNumberFilter(textSearch: string, orderField: string, itemField: string) {
		const defaultCondition: FieldExpression = {
			field: orderField,
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};
		if (textSearch.includes('/')) {
			const orderNumber = textSearch.slice(0, textSearch.lastIndexOf('/'));
			const itemNumber = textSearch.slice(textSearch.lastIndexOf('/') + 1);
			const isNum = /^\d+$/.test(itemNumber);
			if (isNum && orderNumber.length > 0) {
				const orderNumberField: FieldExpression = {
					field: orderField,
					term: `*${ term.escape(orderNumber) }*`,
					quoted: false
				};
				const orderItemNumberField: FieldExpression = {
					field: itemField,
					term: itemNumber,
					quoted: itemNumber === ''
				};
				const orderNumberNode: NodeExpression = {
					left: orderNumberField,
					operator: 'AND',
					parenthesized: true,
					right: orderItemNumberField
				};
				const finalOrderNode: NodeExpression = {
					left: orderNumberNode,
					operator: 'OR',
					parenthesized: true,
					right: defaultCondition
				};
				return finalOrderNode;
			}
			return defaultCondition;
		}
		return defaultCondition;
	}

	createLuceneFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;

		const customerOrderNumberField = this.buildOrderNumberFilter(
			textSearch,
			'customerOrderNumber',
			'customerOrderItemNumber'
		);
		const supplierOrderNumberField = this.buildOrderNumberFilter(
			textSearch,
			'supplierOrderNumber',
			'supplierOrderItemNumber'
		);
		const batchReferenceField: FieldExpression = {
			field: 'batchReference',
			term: search,
			quoted: false
		};
		const batchReferenceFieldNode: NodeExpression = {
			left: batchReferenceField,
			operator: 'OR',
			parenthesized: false,
			right: supplierOrderNumberField
		};
		const allNode: NodeExpression = {
			left: customerOrderNumberField,
			operator: 'OR',
			parenthesized: true,
			right: batchReferenceFieldNode
		};
		return luceneToString(allNode);
	}

	getBatchCard(id: string) {
		const fields: readonly FieldOfBatch[] = [
			...batchCardFields,
			...batchShipmentFields
		];
		const batchId = encodeURIComponent(id);
		const url = new URL(`${ GET_BATCHES_URL }/${ batchId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return this.http.get<Batch>(url.toString());
	}

	getBatchCount(textSearch: string) {
		const url = new URL(GET_BATCHES_URL);
		const params = url.searchParams;
		if (textSearch !== '') {
			const filter = this.createLuceneFilter(textSearch);
			params.append('filter', filter);
		}
		params.append('onlyCount', 'true');

		return this.http.get<PaginatedResult<Batch>>(url.toString());
	}

	async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.activedRoute,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async batchDatacenterFilter(document: Document, defaultView: UserDatacenterView) {
		const filtersList: DatacenterFilterType[] = [];
		const batchNumberValues = [];
		const orderNumberValues = [];
		for (const documentLine of document.documentLines) {
			const batchNumberWithoutPrefixZero = documentLine.batchNumber.startsWith('0')
				? documentLine.batchNumber.replace(/^0+/, '') : documentLine.batchNumber;
			const batchNumberWithPrefixZero = !documentLine.batchNumber.startsWith('0')
				? `0${ documentLine.batchNumber }` : documentLine.batchNumber;
			batchNumberValues.push(batchNumberWithoutPrefixZero, batchNumberWithPrefixZero);
			if (document.type === 'IN' || document.type === 'DA') {
				orderNumberValues.push(
					{
						reference: documentLine.supplierOrderReference
					}
				);
			} else {
				orderNumberValues.push(
					{
						reference: document.supplierOrderReference
					}
				);
			}
		}
		const batchFilter: DatacenterFilterType = {
			id: 'batchReference',
			values: batchNumberValues
		};
		const orderNumberFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: orderNumberValues
		};
		filtersList.push(batchFilter, orderNumberFilter);
		defaultView.filterState = filtersList;
		const route = '/order-tracking/batch/datacenter';
		await this.navigation(defaultView, route);
	}

	constructLandTransitBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '3',
			quoted: true
		};
		const statusGroup1: FieldExpression = {
			field: 'batchStatus',
			term: '11',
			quoted: true
		};
		const statusGroup2: FieldExpression = {
			field: 'batchStatus',
			term: '06',
			quoted: true
		};
		const statusGroup3: FieldExpression = {
			field: 'batchStatus',
			term: '16',
			quoted: true
		};
		const statusGroup4: FieldExpression = {
			field: 'batchStatus',
			term: '41',
			quoted: true
		};
		const statusGroups: NodeExpression = {
			left: statusGroup1,
			operator: 'OR',
			right: statusGroup2,
			parenthesized: false
		};
		const mergedStatusGroups: NodeExpression = {
			left: statusGroups,
			operator: 'OR',
			right: statusGroup3,
			parenthesized: false
		};
		const finalStatusGroups: NodeExpression = {
			left: mergedStatusGroups,
			operator: 'OR',
			right: statusGroup4,
			parenthesized: true
		};

		const finalFilterExpression: NodeExpression = {
			left: finalStatusGroups,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};
		return luceneToString(finalFilterExpression);
	}

	constructAtReshipperBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '3',
			quoted: true
		};
		const statusGroup: FieldExpression = {
			field: 'batchStatus',
			term: '13',
			quoted: true
		};

		const finalFilterExpression: NodeExpression = {
			left: statusGroup,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};
		return luceneToString(finalFilterExpression);
	}

	constructOnVesselBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '3',
			quoted: true
		};
		const statusGroup1: FieldExpression = {
			field: 'batchStatus',
			term: '21',
			quoted: true
		};
		const statusGroup2: FieldExpression = {
			field: 'batchStatus',
			term: '22',
			quoted: true
		};
		const statusGroup3: FieldExpression = {
			field: 'batchStatus',
			term: '25',
			quoted: true
		};
		const statusGroup4: FieldExpression = {
			field: 'batchStatus',
			term: '26',
			quoted: true
		};
		const statusGroup5: FieldExpression = {
			field: 'batchStatus',
			term: '27',
			quoted: true
		};
		const statusGroups: NodeExpression = {
			left: statusGroup1,
			operator: 'OR',
			right: statusGroup2,
			parenthesized: false
		};
		const mergedStatusGroup1: NodeExpression = {
			left: statusGroups,
			operator: 'OR',
			right: statusGroup3,
			parenthesized: false
		};
		const mergedStatusGroup2: NodeExpression = {
			left: mergedStatusGroup1,
			operator: 'OR',
			right: statusGroup4,
			parenthesized: false
		};

		const mergedStatusGroup3: NodeExpression = {
			left: mergedStatusGroup2,
			operator: 'OR',
			right: statusGroup5,
			parenthesized: true
		};

		const finalFilterExpression: NodeExpression = {
			left: mergedStatusGroup3,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};
		return luceneToString(finalFilterExpression);
	}

	constructOverseasTransitBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '3',
			quoted: true
		};
		const statusGroup1: FieldExpression = {
			field: 'batchStatus',
			term: '31',
			quoted: true
		};
		const statusGroup2: FieldExpression = {
			field: 'batchStatus',
			term: '33',
			quoted: true
		};
		const statusGroup3: FieldExpression = {
			field: 'batchStatus',
			term: '34',
			quoted: true
		};
		const statusGroup4: FieldExpression = {
			field: 'batchStatus',
			term: '36',
			quoted: true
		};
		const statusGroup5: FieldExpression = {
			field: 'batchStatus',
			term: '37',
			quoted: true
		};
		const statusGroups: NodeExpression = {
			left: statusGroup1,
			operator: 'OR',
			right: statusGroup2,
			parenthesized: false
		};
		const mergedStatusGroup1: NodeExpression = {
			left: statusGroups,
			operator: 'OR',
			right: statusGroup3,
			parenthesized: false
		};
		const mergedStatusGroup2: NodeExpression = {
			left: mergedStatusGroup1,
			operator: 'OR',
			right: statusGroup4,
			parenthesized: false
		};

		const mergedStatusGroup3: NodeExpression = {
			left: mergedStatusGroup2,
			operator: 'OR',
			right: statusGroup5,
			parenthesized: true
		};

		const finalFilterExpression: NodeExpression = {
			left: mergedStatusGroup3,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};
		return luceneToString(finalFilterExpression);
	}

	constructTransitToCustomerBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '3',
			quoted: true
		};
		const statusGroup1: FieldExpression = {
			field: 'batchStatus',
			term: '12',
			quoted: true
		};
		const statusGroup2: FieldExpression = {
			field: 'batchStatus',
			term: '15',
			quoted: true
		};
		const statusGroup3: FieldExpression = {
			field: 'batchStatus',
			term: '42',
			quoted: true
		};
		const statusGroup4: FieldExpression = {
			field: 'batchStatus',
			term: '43',
			quoted: true
		};
		const statusGroups: NodeExpression = {
			left: statusGroup1,
			operator: 'OR',
			right: statusGroup2,
			parenthesized: false
		};
		const mergedStatusGroups: NodeExpression = {
			left: statusGroups,
			operator: 'OR',
			right: statusGroup3,
			parenthesized: false
		};
		const finalStatusGroups: NodeExpression = {
			left: mergedStatusGroups,
			operator: 'OR',
			right: statusGroup4,
			parenthesized: true
		};

		const finalFilterExpression: NodeExpression = {
			left: finalStatusGroups,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};
		return luceneToString(finalFilterExpression);
	}

	constructConsignmentStockBatchesFilter() {
		const batchStatusGroup: FieldExpression = {
			field: 'batchStatusGroup',
			term: '4',
			quoted: true
		};
		const statusGroup1: FieldExpression = {
			field: 'batchStatus',
			term: '07',
			quoted: true
		};
		const statusGroup2: FieldExpression = {
			field: 'batchStatus',
			term: '38',
			quoted: true
		};

		const mergedStatusGroups: NodeExpression = {
			left: statusGroup1,
			operator: 'OR',
			right: statusGroup2,
			parenthesized: true
		};

		const finalStatusGroups: NodeExpression = {
			left: mergedStatusGroups,
			operator: 'AND',
			right: batchStatusGroup,
			parenthesized: true
		};

		return luceneToString(finalStatusGroups);
	}

	getAggregatedQuantityForBatches(filter: string | undefined) {
		const url = new URL(`${ GET_DATACENTER_BATCHES_URL_V5 }:aggregates`);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<AggregateBatch>(url.toString());
	}

	getStockAlertIcon(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		switch (stockAlert) {
		case '1':
			return 'in_stock';
		case '2':
			return 'in_stock_orange';
		case '3':
			return 'in_stock_red';
		default:
			return '';
		}
	}

	getStockAlertTranslation(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		switch (stockAlert) {
		case '1':
			return 'stock-alert.green';
		case '2':
			return 'stock-alert.orange';
		case '3':
			return 'stock-alert.red';
		default:
			return '';
		}
	}
}
